<template>
  <v-card class="elevation-0">
    <div id="loadingOverlay" :style="loadingOverlayStyle">
        <div class="loader" :style="spinnerStyle"></div>
    </div>
    
    <v-container fluid>
      <v-row justify="center">
        <v-col cols="12" md="8" lg="7">
          <v-alert 
            icon="mdi-information-outline"
            color="blue" 
            outlined
            class="mb-8"
          >
            <div class="text-body-2">
              <strong>Important:</strong> Game download links will expire after 1 week. You will have to rebuild games after this time if you wish to download again.
            </div>
          </v-alert>

          <v-card outlined class="pa-6" elevation="1">
            <v-switch
              v-model="emailAddressActive" 
              :label="$t('details.emailAddress')"
              color="primary"
              class="mb-4"
              hide-details
            ></v-switch>
            
            <v-text-field
              v-model="emailAddress"
              :label="$t('details.emailPlaceholder')"
              outlined
              :disabled="!emailAddressActive"
              :error="emailAddressActive && emailAddress && !emailAddressValid"
              :error-messages="emailAddressActive && emailAddress && !emailAddressValid ? [$t('details.invalidEmail')] : []"
              prepend-inner-icon="mdi-email-outline"
              class="mb-6"
            ></v-text-field>
            
            <v-divider class="mb-6"></v-divider>
            
            <v-row>
              <v-col cols="12" sm="6">
                <v-btn 
                  @click="triggerGameConfirmation" 
                  :disabled="buildWheelDisabled"
                  color="primary"
                  large
                  block
                  elevation="2"
                  class="text-capitalize font-weight-medium"
                >
                  <v-icon left>mdi-wrench</v-icon>
                  {{ $t('action.buildGame') }}
                </v-btn>
              </v-col>
              
              <v-col cols="12" sm="6">
                <v-btn 
                  @click="downloadGame" 
                  :disabled="downloadGameDisabled"
                  color="success"
                  large
                  block
                  elevation="2"
                  class="text-capitalize font-weight-medium"
                >
                  <v-icon left>mdi-download</v-icon>
                  {{ $t('action.downloadGame') }}
                </v-btn>
              </v-col>
            </v-row>
            
            <v-row>
              <v-col cols="12">
                <v-btn 
                  @click="redirectGameRules" 
                  :disabled="gameRulesDisabled"
                  color="#2196F3"
                  large
                  block
                  outlined
                  class="text-capitalize font-weight-medium"
                  :style="!gameRulesDisabled ? 'color: #2196F3 !important' : ''"
                >
                  <v-icon left>mdi-clipboard-edit-outline</v-icon>
                  {{ $t('action.goToRules') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card>

        </v-col>
      </v-row>
    </v-container>

    <!-- Build Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h5 text-center primary--text pa-6">
          {{ $t('action.confirmGame') }}
        </v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text class="pa-6">
          <v-list dense>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ $t('details.wheelName') }}</v-list-item-title>
                <v-list-item-subtitle>{{ getSelectedGame.name }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ $t('details.numberOfTiers') }}</v-list-item-title>
                <v-list-item-subtitle>{{ getSelectedGame.tiers ? getSelectedGame.tiers.length + 1 : 1 }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">Base Wheel Segment Count</v-list-item-title>
                <v-list-item-subtitle>{{ getSelectedGame.no_of_segments }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ $t('details.bonusSegmentCount') }}</v-list-item-title>
                <v-list-item-subtitle>{{ getNumberOfBonusSegments(getSelectedGame.segments) }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <div v-for="(tier, index) in getSelectedGame.tiers" :key="`tier_${index}`">
              <v-divider class="my-2"></v-divider>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold">Tier {{ index + 2 }} Segment Count</v-list-item-title>
                  <v-list-item-subtitle>{{ getSelectedGame.tiers[index].segments.length }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold">Tier {{ index + 2 }} {{ $t('details.bonusSegmentCount') }}</v-list-item-title>
                  <v-list-item-subtitle>{{ getNumberOfBonusSegments(getSelectedGame.tiers[index].segments) }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </div>
          </v-list>
        </v-card-text>
        
        <v-divider></v-divider>
        
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn 
            color="grey" 
            text 
            large
            @click="confirmDialog = false"
            class="mr-4"
          >
            Cancel
          </v-btn>
          <v-btn 
            color="primary" 
            large
            elevation="2"
            @click="buildGame"
          >
            Confirm Build
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
    data() {
        return {
            timerDisable: false,
            downloading: false,
            confirmDialog: false,
            enableDownload: false,
            presignedUrl: null,
            emailAddress: null,
            emailAddressActive: false,
            hasGameChanged: false,
        }
    },
    mounted() {
        // Only set presignedUrl if game hasn't changed since last build
        this.checkForGameChanges();
        if (!this.hasGameChanged) {
            this.presignedUrl = this.getSelectedGame.presignedUrl;
        }
    },
    computed: {
        ...mapGetters(['getGameJSON', "getSelectedGame", "getPresignedUrl", "getBeforeUnloadEventFunction", "getOriginalGameSnapshot"]),
        gameJSON(){
            return this.getGameJSON && this.getGameJSON.segments && this.getGameJSON.segments.length >=3;
        },
        getUser() {
            try {
                const stored = localStorage.getItem("user");
                if (!stored) return null;
                const parsed = JSON.parse(stored);
                return parsed && parsed.name ? parsed.name : null;
            } catch (e) {
                return null;
            }
        },
        gameId(){
            return this.getSelectedGame ? this.getSelectedGame.wheelGameId : null;
        },
        spinnerStyle() {
            return this.downloading ? 'pointer-events: none' : '';
        },
        loadingOverlayStyle() {
            return this.downloading ? '' : 'display: none';
        },
        emailAddressValid() {
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            return emailRegex.test(this.emailAddress);
        },
        buildWheelDisabled() {
            return !this.gameId || 
                   (this.emailAddressActive && !this.emailAddressValid) ||
                   (this.getOriginalGameSnapshot && !this.hasGameChanged && !this.downloadGameDisabled) ||  // If game snapshot exists and hasn't changed, disable build btn
                   !this.downloadGameDisabled
                   || this.buildInProgress; // If download game is disabled, disable build btn
        },
        downloadGameDisabled() {
            return this.hasGameChanged || 
                (this.presignedUrl == null || this.presignedUrl == '' || this.presignedUrl == 'null');
        },
        gameRulesDisabled() {
            return !this.gameId || this.getSelectedGame.status !== "Complete";
        }   
    },
    methods: {
        ...mapActions(["generateGameJSON", "uploadWheelGame", "downloadGameZip", "saveGame", "deleteContent", "uploadGameZip", "getGame", "clearOriginalGameSnapshot", "setOriginalGameSnapshot"]),
        checkForGameChanges() {
            if (!this.getOriginalGameSnapshot) {
                this.hasGameChanged = false;
                return;
            }
            
            // Create hash of important fields from both original and current game
            const createGameHash = (game) => {
                const importantFields = {
                    name: game.name,
                    no_of_segments: game.no_of_segments,
                    idle: game.idle,
                    animation: game.animation,
                    background: game.background,
                    backgroundOverlay: game.backgroundOverlay,
                    wheelOverlay: game.wheelOverlay,
                    foreground: game.foreground,
                    segment_definitions: game.segment_definitions,
                    segments: game.segments,
                    tiers: game.tiers,
                    selectedGameRule: game.selectedGameRule,
                    selectedGameSubRule: game.selectedGameSubRule
                };
                
                const cleanedFields = removeSrcProperties(importantFields);
                return JSON.stringify(cleanedFields);
            };

            // Remove all .src properties from nested objects
            const removeSrcProperties = (obj) => {
                if (obj === null || obj === undefined) {
                    return obj;
                }
                
                if (Array.isArray(obj)) {
                    return obj.map(item => removeSrcProperties(item));
                }
                
                if (typeof obj === 'object') {
                    const cleaned = {};
                    for (const key in obj) {
                        if (key !== 'src') {
                            cleaned[key] = removeSrcProperties(obj[key]);
                        }
                    }
                    return cleaned;
                }
                
                return obj;
            };
            
            const originalHash = createGameHash(this.getOriginalGameSnapshot);
            const currentHash = createGameHash(this.getSelectedGame);
            
            this.hasGameChanged = originalHash !== currentHash;
            if (this.hasGameChanged) {
                this.buildInProgress = false;
            }
        },
        getNumberOfBonusSegments(segments) {
            let count = 0;
            segments.forEach(segment => {
                if (segment.bonusSegment) count++;
            });
            return count;
        },
        triggerGameConfirmation() {
            if (this.buildWheelDisabled) {
                return;
            }
            this.confirmDialog = true;
        },
        async buildGame() {
            this.downloading = true;
            this.buildInProgress = true;
            this.confirmDialog = false;
            this.presignedUrl = null;
            await this.generateGameJSON();
            await this.uploadWheelGame({'incompleteGame' : false});
            if (this.getSelectedGame.status == "Draft") {
                await this.deleteContent({
                    id: this.getSelectedGame.savedGameId, 
                    explicitUserDeletion: false, 
                    user: this.getUser
                });
            }
            await this.saveGame("complete");  
            await this.uploadGameZip({"emailAddress": this.emailAddressActive ? this.emailAddress : null});
            
            // Take a new snapshot of the current state after successful build
            // This will disable the build button until new changes are made
            await this.setOriginalGameSnapshot(JSON.parse(JSON.stringify(this.getSelectedGame)));
            this.checkForGameChanges();
            
            this.$toast.success("Your game has been added to the queue. Download will be available once it has been built.");
            this.downloading = false;
        },
        async downloadGame() {
            window.removeEventListener('beforeunload', this.getBeforeUnloadEventFunction);
            window.location.href = this.presignedUrl;
            window.addEventListener('beforeunload', this.getBeforeUnloadEventFunction);
        },
        setPresignedUrl(url) {
            this.presignedUrl = url;
            this.buildInProgress = false;
        },
        async redirectGameRules() {
            await this.getGame(this.getSelectedGame.savedGameId);
            await new Promise(resolve => setTimeout(resolve, 100));
            this.$router.push({ name: 'gameRules' });
        }
    }
}
</script>

<style scoped>
#loadingOverlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
}

.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>